import { useState } from 'react'

import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { useT, type StringId } from '../lib/i18n'
import { useGates } from './GateContext'

/**
 * Standing attestations, and the way to withdraw one.
 *
 * **This exists because the gate screen cannot hold it.** `FastingGate` renders
 * the notice only while fasting is unavailable — so the moment an attestation
 * clears the gate, the screen carrying the withdraw button is replaced by the
 * fasting feature itself and the control becomes unreachable. The first version
 * of `SUAP-1214` shipped exactly that: an `attested` branch that could never
 * render, found by driving the round trip in a browser rather than by reading it.
 *
 * `SUAP-1202`'s AC 4 says an attestation is "revocable by the user", and §3.4's
 * gated rows only mean anything if the approval can be taken back — an approval
 * that cannot be withdrawn is a consent nobody really gave. So the durable home
 * is the Me tab, where a person looks for things they have told the app about
 * themselves.
 *
 * Renders nothing when nothing is standing, rather than an empty state: someone
 * who has never attested should not be shown a section about clinical approvals
 * they do not have.
 */
const GATE_COPY: Record<string, StringId> = {
  fasting_attest_hypoglycaemia_risk: 'gate.hypoRisk',
  fasting_attest_clinician: 'gate.clinician',
}

export function AttestationSettings() {
  const { t } = useT()
  const { state, revoke } = useGates()
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  const attested = state?.attested ?? []
  if (attested.length === 0) return null

  const withdraw = async (gate: string) => {
    setBusy(gate)
    setFailed(null)
    try {
      await revoke(gate)
    } catch {
      setFailed(gate)
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-medium text-ink-soft">{t('gate.attested.heading')}</h2>

      {attested.map((gate) => (
        <Card key={gate}>
          <p className="text-ink">{t(GATE_COPY[gate] ?? 'gate.unknown')}</p>
          <p className="mt-2 text-sm text-ink-soft">{t('gate.attested.state')}</p>
          <div className="mt-3">
            <Button variant="ghost" onClick={() => void withdraw(gate)} loading={busy === gate}>
              {t('gate.attested.withdraw')}
            </Button>
          </div>
          {failed === gate ? (
            <p className="mt-2 text-sm text-over" role="alert">
              {t('gate.attest.failed')}
            </p>
          ) : null}
        </Card>
      ))}

      {/* Withdrawing is not a punishment and the copy must not read like one: it
          says what happens next, which is that the feature goes away again. */}
      <p className="text-sm text-ink-soft">{t('gate.attested.withdrawNote')}</p>
    </section>
  )
}
